import HandshakeIcon from '@mui/icons-material/Handshake'
import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  Grid,
  InputAdornment,
  TextField as MuiTextField,
  Typography
} from '@mui/material'
import { FC, useMemo, useState } from 'react'
import {
  Datagrid,
  DeleteButton,
  FunctionField,
  ListContextProvider,
  Pagination,
  TextField,
  useGetList,
  useList,
  useRecordContext,
  useRefresh,
  useTranslate,
} from 'react-admin'
import AddIconButton from '../../components/buttons/AddIconButton'
import AccessPrivilegeChip from '../../components/cards/AccessPrivilegeChip'
import CreateDialog from '../../components/CreateDialog'
import { Deelnemer, Samenwerking } from '../../types/apiRequests'
import CreatePartnerForm from './Forms/CreatePartnerForm'

const CollaborationsPartners: FC = () => {
  const translate = useTranslate()
  const refresh = useRefresh()
  const record = useRecordContext<Samenwerking>()
  const isClosed = record?.status === 'GESLOTEN'
  const [search, setSearch] = useState('')

  const { data, isLoading, error } = useGetList<Deelnemer>(
    `deelnemers/${record?.id}`,
    {
      filter: {},
      pagination: { page: 1, perPage: 1000 },
    }
  )

  const filtered = useMemo(() => {
    if (!data) return []
    if (!search.trim()) return data
    const q = search.toLowerCase()
    return data.filter(entry => entry.organisatie?.naam?.toLowerCase().includes(q))
  }, [data, search])

  const listContext = useList<Deelnemer>({ data: filtered, isLoading, perPage: 10 })

  if (isLoading) return <div />
  if (error) {
    return (
      <Typography color="error">
        {translate('ra.notification.http_error')}
      </Typography>
    )
  }

  return (
    <Grid container spacing={3}>
      <Grid item md={12}>
        <Box
          sx={{
            border: 1,
            borderColor: 'grey.300',
            borderRadius: 1,
            p: 2,
            height: '100%',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              <HandshakeIcon sx={{ mr: '10px' }} fontSize="small" />
              {translate('show.partners')}
              {!isClosed && (
                <CreateDialog
                  resource={`samenwerkingen/${record?.id}/deelnemers`}
                  dialogTitle="data.addPartner"
                  dialogSubtitle={record?.titel}
                  dialogIcon={<HandshakeIcon />}
                  renderTrigger={open => <AddIconButton onClick={open} />}
                >
                  <CreatePartnerForm />
                </CreateDialog>
              )}
            </Typography>
            <MuiTextField
              size="small"
              placeholder={translate('ra.action.search')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 250 }}
            />
          </Box>

          <ListContextProvider value={listContext}>
            <Datagrid rowClick={false} bulkActionButtons={false}>
              <TextField
                label="data.partner"
                source="organisatie.naam"
              />
              <FunctionField
                label={translate('data.access')}
                render={(d: Deelnemer) =>
                  <AccessPrivilegeChip privilege={d.privilege as 'VT' | 'BT' | 'INITIATOR'} />
                }
              />
              {!isClosed && (
                <FunctionField
                  label=''
                  render={(deelnemer: Deelnemer) =>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <DeleteButton
                        record={deelnemer}
                        resource={`samenwerkingen/${record?.id}/deelnemers`}
                        redirect={false}
                        mutationMode="pessimistic"
                        mutationOptions={{
                          onSuccess: () => {
                            refresh()
                          },
                        }}
                      />
                    </Box>
                  }
                />
              )}
            </Datagrid>
            <Pagination />
          </ListContextProvider>
        </Box>
      </Grid>
    </Grid>
  )
}

export default CollaborationsPartners
